import { Logger } from 'utilities';

/**
 * Frame scheduling for the capture pipeline that keeps running while the tab is
 * in the background.
 *
 * `requestAnimationFrame` is **suspended entirely** while `document.hidden` is
 * true — not throttled, not deferred: no callback is ever delivered until the
 * tab is foregrounded again. Every rAF-driven wait in the capture pipeline
 * therefore hung the moment the user switched tabs, which is what made an
 * export appear to pause until they came back to watch it.
 *
 * `setTimeout` is not a usable replacement: Chrome aligns background timers to
 * 1 s, and chained ones drop to one wake-up per minute after the tab has been
 * hidden for five minutes. A 16 ms wait would become a 1–60 s wait.
 *
 * `MessageChannel` messages are plain macrotasks and are **not** throttled by
 * visibility — this is why React's own scheduler is built on them. So when the
 * tab is hidden we synthesize frames by pumping messages through one shared
 * channel and releasing a callback once `VIRTUAL_FRAME_MS` has elapsed.
 *
 * Keeping the synthesized cadence at ~60 Hz is the point: `captureIframe`
 * expresses its readiness thresholds as frame *counts*
 * (`READY_STABILITY_FRAMES`, `MISSING_STABILITY_FRAMES`), and those counts only
 * mean what they are documented to mean if a frame stays worth ~16 ms. Handing
 * the tick loop unthrottled macrotasks instead would burn 30 "frames" in a few
 * milliseconds and let a page be declared `missing` before its data landed.
 *
 * Cost: while hidden, a pending wait spins macrotasks until its deadline. Each
 * yield hands the thread back to the browser first, so the iframe's React work,
 * network callbacks and MutationObserver records all still interleave — but it
 * is a spin, and it only runs while something is actually waiting on a frame.
 */

// Synthesized frame interval while hidden. Mirrors a 60 Hz display so that
// frame-count thresholds elsewhere keep the same wall-clock meaning whether or
// not the tab is in front.
const VIRTUAL_FRAME_MS = 16;

export type ExportFrameHandle = number;

interface ScheduledFrame {
  callback: () => void;
  // Set while a real rAF is pending; cleared once the pump takes over.
  rafId: number | null;
  // performance.now() target for a synthesized frame.
  deadline: number;
}

const scheduled = new Map<ExportFrameHandle, ScheduledFrame>();
let nextHandle: ExportFrameHandle = 1;

// One shared channel for every waiter: N concurrent waits cost one macrotask
// per iteration in total rather than one each.
let pumpChannel: MessageChannel | null = null;
let pumpQueue: Array<() => void> = [];
let visibilityListenerInstalled = false;

const drainPumpQueue = () => {
  const batch = pumpQueue;
  pumpQueue = [];
  batch.forEach(step => {
    try {
      step();
    } catch (err) {
      Logger.error('pdfExport: background frame step failed', err);
    }
  });
};

const enqueuePumpStep = (step: () => void) => {
  // Every browser we support has MessageChannel — React's scheduler would not
  // work without it. The guard is for environments that don't (jsdom), where a
  // clamped timer is the correct degradation: slower frames, never a hang.
  if (typeof MessageChannel === 'undefined') {
    pumpQueue.push(step);
    setTimeout(drainPumpQueue, 0);
    return;
  }
  if (!pumpChannel) {
    pumpChannel = new MessageChannel();
    pumpChannel.port1.onmessage = drainPumpQueue;
  }
  pumpQueue.push(step);
  pumpChannel.port2.postMessage(0);
};

const runFrame = (handle: ExportFrameHandle) => {
  const entry = scheduled.get(handle);
  if (!entry) {
    return;
  }
  scheduled.delete(handle);
  entry.callback();
};

const pumpStep = (handle: ExportFrameHandle) => {
  const entry = scheduled.get(handle);
  if (!entry) {
    return;
  }
  if (performance.now() >= entry.deadline) {
    runFrame(handle);
    return;
  }
  enqueuePumpStep(() => pumpStep(handle));
};

/**
 * Hand every rAF still pending at the moment of backgrounding over to the pump.
 * Those callbacks are already lost — Chrome will not deliver them until the tab
 * is visible again — so they fire on the next macrotask rather than waiting out
 * a fresh interval.
 */
const migratePendingFramesToPump = () => {
  if (!document.hidden) {
    return;
  }
  Array.from(scheduled.keys()).forEach(handle => {
    const entry = scheduled.get(handle);
    if (!entry || entry.rafId === null) {
      return;
    }
    cancelAnimationFrame(entry.rafId);
    entry.rafId = null;
    entry.deadline = performance.now();
    enqueuePumpStep(() => pumpStep(handle));
  });
};

const ensureVisibilityListener = () => {
  if (visibilityListenerInstalled) {
    return;
  }
  visibilityListenerInstalled = true;
  document.addEventListener('visibilitychange', migratePendingFramesToPump);
};

/**
 * Drop-in replacement for `requestAnimationFrame` that also fires while the tab
 * is hidden. Always scheduled on the top-level window: the parent shares the
 * capture iframe's event loop, and using its clock means a wait cannot be
 * orphaned by the iframe being torn down mid-flight.
 */
export const requestExportFrame = (callback: () => void): ExportFrameHandle => {
  ensureVisibilityListener();
  const handle = nextHandle;
  nextHandle += 1;

  if (document.hidden) {
    scheduled.set(handle, { callback, rafId: null, deadline: performance.now() + VIRTUAL_FRAME_MS });
    enqueuePumpStep(() => pumpStep(handle));
    return handle;
  }

  const entry: ScheduledFrame = { callback, rafId: null, deadline: 0 };
  scheduled.set(handle, entry);
  entry.rafId = requestAnimationFrame(() => {
    entry.rafId = null;
    runFrame(handle);
  });
  return handle;
};

export const cancelExportFrame = (handle: ExportFrameHandle | null) => {
  if (handle === null) {
    return;
  }
  const entry = scheduled.get(handle);
  if (!entry) {
    return;
  }
  if (entry.rafId !== null) {
    cancelAnimationFrame(entry.rafId);
  }
  // A queued pump step for this handle becomes a no-op once the entry is gone.
  scheduled.delete(handle);
};

/**
 * Forces a synchronous layout inside the capture iframe.
 *
 * A hidden tab never paints, so the "wait for a frame and the browser will have
 * re-laid out by then" assumption behind `waitTwoExportFrames` does not hold.
 * Reading a layout-dependent property makes the recalculation happen on demand,
 * which is what the callers actually need before they measure `scrollWidth` or
 * hand the subtree to html2canvas.
 */
const flushLayout = (win: Window | null | undefined) => {
  if (!document.hidden) {
    return;
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    win?.document?.documentElement?.offsetHeight;
  } catch {
    // Iframe torn down mid-wait — nothing to flush.
  }
};

/**
 * Waits two frames so the app re-lays out after a DOM change, then flushes
 * layout when the tab is hidden and no real frame will do it for us.
 *
 * `win` is the iframe window whose layout must settle; the frames themselves
 * are scheduled on the parent.
 */
export const waitTwoExportFrames = (win: Window | null | undefined): Promise<void> =>
  new Promise<void>(resolve => {
    requestExportFrame(() =>
      requestExportFrame(() => {
        flushLayout(win);
        resolve();
      })
    );
  });
